import { useState, useRef, useEffect, useCallback } from 'react';
import type { HeaderProps, BreadcrumbItem, UserMenuItem, BreakpointMode } from './types';
import { LocationSwitcher } from './LocationSwitcher';

// ─── Breadcrumb rendering with responsive collapse ──────────────

function renderBreadcrumbs(
  items: BreadcrumbItem[],
  mode: BreakpointMode,
  onNavigate?: (path: string) => void,
) {
  // Mobile: no breadcrumbs (page title shown instead)
  if (mode === 'mobile' || items.length === 0) return null;

  // Tablet: collapse middle segments, show "…" + last 2
  const visibleItems = mode === 'tablet' && items.length > 2
    ? items.slice(-2)
    : items;
  const showEllipsis = mode === 'tablet' && items.length > 2;

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center gap-1 text-sm">
        {showEllipsis && (
          <li className="flex items-center gap-1">
            <span className="text-neutral-400" aria-hidden="true">…</span>
            <span className="text-neutral-400" aria-hidden="true">/</span>
          </li>
        )}
        {visibleItems.map((crumb, i) => {
          const isLast = showEllipsis
            ? i === visibleItems.length - 1
            : i === items.length - 1;
          const absoluteIndex = showEllipsis ? items.length - visibleItems.length + i : i;

          return (
            <li key={`${crumb.label}-${absoluteIndex}`} className="flex items-center gap-1">
              {(showEllipsis ? i > 0 : absoluteIndex > 0) && (
                <span className="text-neutral-400" aria-hidden="true">/</span>
              )}
              {crumb.path && !isLast ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.(crumb.path!);
                  }}
                  className={[
                    'whitespace-nowrap text-neutral-500 hover:text-brand-600',
                    'dark:text-neutral-400 dark:hover:text-brand-400',
                    'transition-colors duration-150',
                  ].join(' ')}
                >
                  {crumb.label}
                </button>
              ) : (
                <span
                  className="truncate font-medium text-neutral-900 dark:text-neutral-100"
                  aria-current={isLast ? 'page' : undefined}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ─── User Menu Dropdown ─────────────────────────────────────────

interface UserMenuProps {
  open: boolean;
  onClose: () => void;
  user: { name: string; role: string };
  items: UserMenuItem[];
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

function UserMenu({ open, onClose, user, items, triggerRef }: UserMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, triggerRef]);

  // Focus first item when opening
  useEffect(() => {
    if (open) {
      setFocusedIndex(0);
      requestAnimationFrame(() => {
        itemRefs.current[0]?.focus();
      });
    }
  }, [open]);

  // Focus the tracked item
  useEffect(() => {
    if (open && focusedIndex >= 0) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(items.length - 1);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          triggerRef.current?.focus();
          break;
        case 'Tab':
          onClose();
          break;
      }
    },
    [items.length, onClose, triggerRef],
  );

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="User menu"
      onKeyDown={handleKeyDown}
      // z-[40], not z-dropdown (10): page filter cards are `relative z-30` (L01)
      // and share this stacking context, so a lower menu hid behind them.
      className={[
        'absolute right-0 top-full z-[40] mt-1 w-56',
        'rounded-md border border-neutral-200 bg-white py-1 shadow-lg',
        'animate-scale-in',
        'dark:border-neutral-700 dark:bg-neutral-800',
      ].join(' ')}
    >
      {/* User info header */}
      <div className="border-b border-neutral-100 px-4 pb-2 pt-1 dark:border-neutral-700">
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {user.name}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {user.role}
        </p>
      </div>

      {/* Menu items */}
      <div className="px-1 pt-1">
        {items.map((item, i) => {
          const isDanger = item.variant === 'danger';
          return (
            <div key={item.id}>
              {item.dividerBefore && (
                <div className="mx-1 my-1 border-t border-neutral-200 dark:border-neutral-700" />
              )}
              <button
                ref={(el) => { itemRefs.current[i] = el; }}
                type="button"
                role="menuitem"
                tabIndex={focusedIndex === i ? 0 : -1}
                onClick={() => {
                  item.onClick();
                  onClose();
                }}
                className={[
                  'flex w-full items-center gap-2 rounded px-3 py-2 text-sm',
                  'min-h-[44px] text-left transition-colors duration-150',
                  isDanger
                    ? 'text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/20'
                    : 'text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-700',
                  focusedIndex === i && !isDanger ? 'bg-neutral-50 dark:bg-neutral-700' : '',
                  focusedIndex === i && isDanger ? 'bg-danger-50 dark:bg-danger-900/20' : '',
                ].join(' ')}
              >
                {item.icon && (
                  <span className="flex h-4 w-4 items-center justify-center" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                {item.label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Header Component ──────────────────────────────────────

export function Header({
  variant = 'default',
  breakpoint = 'desktop',
  title,
  breadcrumbs,
  tenantName,
  isOffline = false,
  notificationCount = 0,
  user,
  locations,
  activeLocation,
  onLocationChange,
  showAllLocations = false,
  allLocationsLabel,
  language = 'en',
  onLanguageChange,
  onMenuToggle,
  onNotificationClick,
  onBreadcrumbNavigate,
  onProfileClick,
  onSettingsClick,
  onSignOut,
  userMenuItems,
  actions,
  showMenuToggle = false,
  menuButtonRef,
}: HeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userAvatarRef = useRef<HTMLButtonElement>(null);

  // Notification badge animation — track previous count
  const prevCountRef = useRef(notificationCount);
  const [badgeAnimating, setBadgeAnimating] = useState(false);

  useEffect(() => {
    if (notificationCount > prevCountRef.current && notificationCount > 0) {
      setBadgeAnimating(true);
      const timer = setTimeout(() => setBadgeAnimating(false), 300);
      return () => clearTimeout(timer);
    }
    prevCountRef.current = notificationCount;
  }, [notificationCount]);

  // Build user menu items
  const resolvedMenuItems: UserMenuItem[] = userMenuItems ?? [
    {
      id: 'profile',
      label: 'Profile',
      onClick: onProfileClick ?? (() => {}),
    },
    {
      id: 'settings',
      label: 'Settings',
      onClick: onSettingsClick ?? (() => {}),
    },
    {
      id: 'sign-out',
      label: 'Sign out',
      onClick: onSignOut ?? (() => {}),
      variant: 'danger',
      dividerBefore: true,
    },
  ];

  // Determine title text for mobile fallback
  const mobileTitle = title
    ?? (breadcrumbs && breadcrumbs.length > 0
      ? breadcrumbs[breadcrumbs.length - 1].label
      : '');

  const isCompact = variant === 'compact';
  const isMobile = breakpoint === 'mobile';
  const isDesktop = breakpoint === 'desktop';

  // Avatar keyboard handler
  const handleAvatarKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setUserMenuOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setUserMenuOpen(false);
    }
  }, []);

  return (
    <header
      role="banner"
      className={[
        'flex items-center gap-2 border-b border-neutral-200',
        'bg-white px-4 dark:border-neutral-700 dark:bg-neutral-900',
        'transition-[height] duration-150',
        // Height: compact=48px, desktop=64px, default=56px
        isCompact ? 'h-12' : isDesktop ? 'h-16' : 'h-header',
      ].join(' ')}
    >
      {/* ─── Hamburger menu (tablet + mobile) ────────────────────── */}
      {showMenuToggle && (
        <button
          ref={menuButtonRef}
          type="button"
          onClick={onMenuToggle}
          className={[
            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded',
            'text-neutral-600 hover:bg-neutral-100',
            'dark:text-neutral-300 dark:hover:bg-neutral-800',
            'transition-colors duration-150',
          ].join(' ')}
          aria-label="Toggle navigation menu"
        >
          {/* TODO: replace with icon library */}
          <span className="text-xl" aria-hidden="true">☰</span>
        </button>
      )}

      {/* ─── Breadcrumbs / Title ─────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {isMobile ? (
          // Mobile: page title only
          mobileTitle ? (
            <span className={[
              'truncate font-semibold text-neutral-900 dark:text-neutral-100',
              isCompact ? 'text-base' : 'text-lg',
            ].join(' ')}>
              {mobileTitle}
            </span>
          ) : null
        ) : breadcrumbs && breadcrumbs.length > 0 ? (
          renderBreadcrumbs(breadcrumbs, breakpoint, onBreadcrumbNavigate)
        ) : title ? (
          <span className={[
            'truncate font-semibold text-neutral-900 dark:text-neutral-100',
            isCompact ? 'text-base' : 'text-lg',
          ].join(' ')}>
            {title}
          </span>
        ) : null}
      </div>

      {/* ─── Header actions slot ─────────────────────────────────── */}
      {actions && (
        <div className={[
          'flex items-center gap-2',
          isMobile ? 'hidden' : '',
        ].join(' ')}>
          {actions}
        </div>
      )}

      {/* ─── Language indicator (desktop only) ───────────────────── */}
      {onLanguageChange && isDesktop && (
        <button
          type="button"
          onClick={() => {
            const langs: Array<'en' | 'hi' | 'ta'> = ['en', 'hi', 'ta'];
            const next = langs[(langs.indexOf(language) + 1) % langs.length];
            onLanguageChange(next);
          }}
          className={[
            'flex h-8 items-center rounded border border-neutral-200 px-2',
            'text-xs font-medium uppercase tracking-wide text-neutral-600',
            'hover:bg-neutral-100 dark:border-neutral-700',
            'dark:text-neutral-300 dark:hover:bg-neutral-800',
            'transition-colors duration-150',
          ].join(' ')}
          aria-label={`Current language: ${language}. Click to switch.`}
        >
          {language}
        </button>
      )}

      {/* ─── Location switcher (desktop only) ────────────────────── */}
      {isDesktop && locations && locations.length > 0 && onLocationChange && (
        <LocationSwitcher
          locations={locations}
          activeId={activeLocation ?? 'all'}
          onChange={onLocationChange}
          showAll={showAllLocations}
          allLabel={allLocationsLabel}
        />
      )}

      {/* ─── Tenant name + offline dot (desktop only) ────────────── */}
      {isDesktop && tenantName && (
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {tenantName}
          </span>
          {isOffline && (
            <span
              className="h-2.5 w-2.5 rounded-full bg-danger-500 animate-pulse-soft"
              title="Offline"
              aria-label="Offline"
            />
          )}
        </div>
      )}

      {/* ─── Notification bell ───────────────────────────────────── */}
      <button
        type="button"
        onClick={onNotificationClick}
        className={[
          'relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded',
          'text-neutral-600 hover:bg-neutral-100',
          'dark:text-neutral-300 dark:hover:bg-neutral-800',
          'transition-colors duration-150',
        ].join(' ')}
        aria-label={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ''}`}
      >
        {/* TODO: replace with icon library */}
        <span className="text-lg" aria-hidden="true">🔔</span>

        {notificationCount > 0 && (
          <>
            {/* Full badge (tablet + desktop) */}
            <span
              className={[
                'absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center',
                'justify-center rounded-full bg-danger-500 px-1',
                'text-[10px] font-bold text-white',
                badgeAnimating ? 'animate-checkmark' : '',
                // Hidden on mobile
                isMobile ? 'hidden' : '',
              ].join(' ')}
              aria-hidden="true"
            >
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>

            {/* Dot indicator (mobile only) */}
            {isMobile && (
              <span
                className={[
                  'absolute right-1 top-1 h-2 w-2 rounded-full bg-danger-500',
                  badgeAnimating ? 'animate-checkmark' : '',
                ].join(' ')}
                aria-hidden="true"
              />
            )}
          </>
        )}
      </button>

      {/* ─── User avatar + dropdown (hidden on mobile) ───────────── */}
      {user && !isMobile && (
        <div className="relative flex-shrink-0">
          <button
            ref={userAvatarRef}
            type="button"
            onClick={() => setUserMenuOpen((prev) => !prev)}
            onKeyDown={handleAvatarKeyDown}
            className={[
              'flex h-9 w-9 items-center justify-center rounded-full',
              'bg-brand-100 text-sm font-semibold text-brand-700',
              'hover:bg-brand-200',
              'dark:bg-brand-900 dark:text-brand-300 dark:hover:bg-brand-800',
              'transition-colors duration-150',
            ].join(' ')}
            aria-label={`User menu for ${user.name}`}
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
          >
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="h-9 w-9 rounded-full object-cover"
              />
            ) : (
              user.name.charAt(0).toUpperCase()
            )}
          </button>

          <UserMenu
            open={userMenuOpen}
            onClose={() => setUserMenuOpen(false)}
            user={user}
            items={resolvedMenuItems}
            triggerRef={userAvatarRef}
          />
        </div>
      )}
    </header>
  );
}
